import React, { useState } from "react";
import { View, StyleSheet, Pressable, Text } from "react-native";
import { DotsThreeVertical } from "@phosphor-icons/react";
import { colors, radii, spacing, webTransitions } from "../../../styles/design-system";

export type InlineContextMenuTone = "default" | "danger";

export interface InlineContextMenuItem {
  id: string;
  label: string;
  tone?: InlineContextMenuTone;
  onPress?: () => void;
  icon?: React.ReactNode;
}

export interface InlineContextMenuProps {
  items: InlineContextMenuItem[];
  /**
   * Optional controlled open state. When provided, the component
   * will not manage its own open/close state internally.
   */
  open?: boolean;
  /**
   * Called whenever the menu requests to change its open state (e.g. trigger or item click).
   */
  onOpenChange?: (open: boolean) => void;
}

const InlineContextMenu: React.FC<InlineContextMenuProps> = ({ items, open: controlledOpen, onOpenChange }) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = (nextOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }
    if (onOpenChange) {
      onOpenChange(nextOpen);
    }
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleItemPress = (item: InlineContextMenuItem) => {
    if (item.onPress) {
      item.onPress();
    }
    setOpen(false);
  };

  return (
    <View style={styles.container}>
      <Pressable
        accessibilityRole="button"
        onPress={handleToggle}
        style={({ pressed }) => [styles.trigger, pressed && { backgroundColor: colors.grey[100] }]}>
        <DotsThreeVertical size={18} color={colors.grey[500]} />
      </Pressable>
      {open && (
        <View style={styles.menu}>
          {items.map((item, index) => (
            <Pressable
              key={item.id}
              onPress={() => handleItemPress(item)}
              style={(state) => {
                const { pressed } = state;
                const hovered = (state as { hovered?: boolean }).hovered ?? false;
                return [
                  styles.menuItem,
                  index > 0 && styles.menuItemWithDivider,
                  (hovered || pressed) && styles.menuItemHovered,
                ];
              }}>
              <View style={styles.menuItemContent}>
                {item.icon ? <View style={styles.iconContainer}>{item.icon}</View> : null}
                <Text style={[styles.menuItemLabel, item.tone === "danger" && styles.menuItemLabelDanger]}>
                  {item.label}
                </Text>
              </View>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "relative",
  },
  trigger: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  menu: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: spacing.xxs,
    backgroundColor: colors.white,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.grey[200],
    paddingVertical: 4,
    paddingHorizontal: 0,
    overflow: "hidden",
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 4,
    zIndex: 1000,
  },
  menuItem: {
    paddingVertical: 8,
    paddingHorizontal: spacing.sm,
    ...webTransitions.fast,
  },
  menuItemWithDivider: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  menuItemHovered: {
    backgroundColor: colors.grey[50],
  },
  menuItemContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  iconContainer: {
    marginRight: spacing.xs,
  },
  menuItemLabel: {
    fontSize: 13,
    color: colors.grey[800],
  },
  menuItemLabelDanger: {
    color: colors.error[500],
  },
});

export default InlineContextMenu;
